<template>
  <div class="cfg-tab">
    <div class="cfg-intro">
      <v-icon>mdi-information-outline</v-icon>
      <div>
        <p>
          În „Demers nou” utilizatorii scriu modelul liber; catalogul doar le sugerează texte, de exemplu
          „Căști Logitech H390” (categoria + modelul).
        </p>
        <p>Redenumirea sau ștergerea unui model nu schimbă echipamentele existente: fiecare își păstrează textul.</p>
      </div>
    </div>

    <div class="cfg-chips" role="radiogroup" aria-label="Tipul de echipament">
      <button
        v-for="t in typeChoices"
        :key="t.id"
        type="button"
        role="radio"
        class="cfg-chip"
        :class="{ 'is-active': typeId === t.id }"
        :aria-checked="typeId === t.id"
        @click="typeId = t.id"
      >
        <span class="cfg-chip__name">{{ t.type }}</span><span class="cfg-chip__count">{{ kindCount(t) }}</span>
      </button>
    </div>

    <v-progress-linear v-if="loading || catalogLoading" indeterminate color="primary" class="mb-2" />

    <v-row v-if="typeId" dense>
      <v-col cols="12" md="5">
        <section class="form-section">
          <div class="form-section__label">
            <v-icon small>mdi-shape-outline</v-icon>Categorii {{ typeLabel }}
            <span class="cat__count">{{ kinds.length }}</span>
          </div>
          <div class="cat__add">
            <v-text-field
              v-model="newKindName"
              label="Categorie nouă"
              prepend-inner-icon="mdi-plus-circle-outline"
              placeholder="de exemplu: Căști, Laptop"
              outlined
              dense
              hide-details
              maxlength="50"
              @keyup.enter="addKind"
            ></v-text-field>
            <v-btn class="btn-green cfg-btn" height="40" :disabled="!newKindName.trim()" :loading="saving" @click="addKind">
              <v-icon left>mdi-plus</v-icon>Adaugă
            </v-btn>
          </div>
          <div class="cfg-list cat__list">
            <div v-if="!kinds.length" class="cfg-empty">Nicio categorie pentru acest tip.</div>
            <div v-for="k in kinds" :key="k.id" class="cfg-row cat__row">
              <template v-if="editingKindId === k.id">
                <v-text-field
                  v-model="editKindName"
                  aria-label="Categoria"
                  outlined
                  dense
                  hide-details
                  autofocus
                  maxlength="50"
                  class="cat__edit"
                  @keyup.enter="saveKind(k)"
                  @keyup.esc="editingKindId = null"
                ></v-text-field>
                <div class="cfg-row__actions">
                  <v-btn icon small color="primary" aria-label="Salvează categoria" :disabled="saving" @click="saveKind(k)">
                    <v-icon small>mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon small aria-label="Anulează editarea" @click="editingKindId = null">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-else>
                <div class="cfg-row__main">
                  <span class="cfg-row__name">{{ k.name }}</span>
                  <div class="cfg-usage cat__usage">{{ modelCountText(k.id) }}</div>
                </div>
                <div class="cfg-row__actions">
                  <v-btn icon small color="primary" aria-label="Redenumește categoria" @click="startEditKind(k)">
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon small color="error" aria-label="Șterge categoria" @click="removeKind(k)">
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </template>
            </div>
          </div>
        </section>
      </v-col>

      <v-col cols="12" md="7">
        <section class="form-section">
          <div class="form-section__label">
            <v-icon small>mdi-tag-multiple-outline</v-icon>Modele sugerate
            <span class="cat__count">{{ models.length }}</span>
          </div>
          <v-row dense class="mb-1">
            <v-col cols="12" sm="4">
              <v-select
                v-model="newModelKind"
                :items="kinds"
                item-text="name"
                item-value="id"
                label="Categoria"
                prepend-inner-icon="mdi-shape-outline"
                no-data-text="Adăugați întâi o categorie"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" sm="5">
              <v-text-field
                v-model="newModelName"
                label="Model nou"
                prepend-inner-icon="mdi-tag-outline"
                placeholder="de exemplu: Logitech H390"
                outlined
                dense
                hide-details
                maxlength="99"
                @keyup.enter="addModel"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-btn
                class="btn-green cfg-btn"
                block
                height="40"
                :disabled="!newModelKind || !newModelName.trim()"
                :loading="saving"
                @click="addModel"
              >
                <v-icon left>mdi-plus</v-icon>Adaugă
              </v-btn>
            </v-col>
          </v-row>

          <v-text-field
            v-if="models.length > 8"
            v-model="modelSearch"
            class="cfg-search cat__search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Caută un model"
            aria-label="Caută un model"
            outlined
            dense
            single-line
            hide-details
            clearable
          ></v-text-field>

          <div class="cfg-list cat__list">
            <div v-if="!filteredModels.length" class="cfg-empty">
              {{ models.length ? "Niciun model găsit." : "Niciun model pentru acest tip." }}
            </div>
            <div v-for="m in filteredModels" :key="m.id" class="cfg-row cat__row">
              <template v-if="editingModelId === m.id">
                <div class="cat__edit-model">
                  <v-select
                    v-model="editModelKind"
                    :items="kinds"
                    item-text="name"
                    item-value="id"
                    aria-label="Categoria"
                    outlined
                    dense
                    hide-details
                  ></v-select>
                  <v-text-field
                    v-model="editModelName"
                    aria-label="Model"
                    outlined
                    dense
                    hide-details
                    autofocus
                    maxlength="99"
                    @keyup.enter="saveModel(m)"
                    @keyup.esc="editingModelId = null"
                  ></v-text-field>
                </div>
                <div class="cfg-row__actions">
                  <v-btn icon small color="primary" aria-label="Salvează modelul" :disabled="saving" @click="saveModel(m)">
                    <v-icon small>mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon small aria-label="Anulează editarea" @click="editingModelId = null">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-else>
                <div class="cfg-row__main">
                  <span class="cat__kind">{{ m.kindName }}</span> <span class="cfg-row__name cat__model">{{ m.name }}</span>
                </div>
                <div class="cfg-row__actions">
                  <v-btn icon small color="primary" aria-label="Editează modelul" @click="startEditModel(m)">
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon small color="error" aria-label="Șterge modelul" @click="removeModel(m)">
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </template>
            </div>
          </div>
        </section>
      </v-col>
    </v-row>

    <RemoveDialog ref="remove" />
  </div>
</template>

<script>
import * as api from "./api";
import configMixin from "./configMixin";
import RemoveDialog from "./RemoveDialog.vue";
import { PERIPHERAL_EQUIPMENT_TYPE_ID } from "@/config/periferice";
import { bySortOrder, count, plain } from "./format";

export default {
  name: "CatalogTab",
  components: { RemoveDialog },
  mixins: [configMixin],
  data() {
    return {
      types: [],
      typeId: null,
      catalogLoading: false,
      kinds: [],
      models: [],
      modelSearch: "",
      newKindName: "",
      editingKindId: null,
      editKindName: "",
      newModelKind: null,
      newModelName: "",
      editingModelId: null,
      editModelKind: null,
      editModelName: "",
    };
  },
  computed: {
    // hidden types only when they still have a catalog
    typeChoices() {
      return [...this.types].sort(bySortOrder).filter((t) => !t.is_hidden || this.kindCount(t) > 0 || t.id === this.typeId);
    },
    typeLabel() {
      const t = this.types.find((x) => x.id === this.typeId);
      return t ? `(${t.type})` : "";
    },
    filteredModels() {
      const term = plain(this.modelSearch);
      return term ? this.models.filter((m) => plain(m.fullName).includes(term)) : this.models;
    },
  },
  watch: {
    typeId(id, old) {
      if (old === null) return; // the first pick is loaded by load()
      this.editingKindId = null;
      this.editingModelId = null;
      this.newModelKind = null;
      this.modelSearch = "";
      this.loadCatalog();
    },
  },
  methods: {
    async load() {
      this.types = await api.getEquipmentTypes();
      if (!this.types.some((t) => t.id === this.typeId)) {
        const wanted = Number(this.$route.query.type);
        const pick =
          this.types.find((t) => t.id === wanted) ||
          this.types.find((t) => t.id === PERIPHERAL_EQUIPMENT_TYPE_ID) ||
          this.types.find((t) => this.kindCount(t) > 0) ||
          this.types[0];
        this.typeId = pick ? pick.id : null;
      }
      await this.loadCatalog();
    },
    async loadCatalog() {
      if (!this.typeId) return;
      const id = this.typeId;
      this.catalogLoading = true;
      try {
        const [kinds, models] = await Promise.all([api.getCatalogKinds(id), api.getCatalogModels(id)]);
        if (id !== this.typeId) return; // the user switched meanwhile
        this.kinds = kinds;
        this.models = models;
        if (!kinds.some((k) => k.id === this.newModelKind)) this.newModelKind = kinds.length === 1 ? kinds[0].id : null;
      } catch (error) {
        this.fail(error, "Eroare la încărcarea catalogului");
      } finally {
        this.catalogLoading = false;
      }
    },
    kindCount(t) {
      if (t.id === this.typeId) return this.kinds.length;
      return (t.usage && t.usage.catalogKinds) || 0;
    },
    modelCountText(kindId) {
      const n = this.models.filter((m) => m.kind_id === kindId).length;
      return n ? count(n, "model", "modele") : "fără modele";
    },

    // Categories
    async addKind() {
      const name = this.newKindName.trim();
      if (!name) return;
      const ok = await this.change(() => api.createCatalogKind(name, this.typeId), {
        done: `Categoria „${name}” a fost adăugată`,
        fallback: "Eroare la adăugarea categoriei",
      });
      if (ok) this.newKindName = "";
    },
    startEditKind(k) {
      this.editingKindId = k.id;
      this.editKindName = k.name;
    },
    async saveKind(k) {
      if (this.editKindName.trim() === k.name) {
        this.editingKindId = null;
        return;
      }
      const ok = await this.change(() => api.updateCatalogKind(k.id, this.editKindName), {
        done: "Categoria a fost redenumită",
        fallback: "Eroare la salvarea categoriei",
      });
      if (ok) this.editingKindId = null;
    },
    async removeKind(k) {
      const n = this.models.filter((m) => m.kind_id === k.id).length;
      const choice = await this.$refs.remove.ask({
        title: n ? `Categoria „${k.name}” nu poate fi ștearsă` : `Ștergeți categoria „${k.name}”?`,
        text: n
          ? `Are ${count(n, "model", "modele")}. Ștergeți-le sau mutați-le mai întâi în altă categorie.`
          : "Echipamentele existente nu se schimbă.",
        canDelete: !n,
      });
      if (choice === "delete") {
        await this.change(() => api.deleteCatalogKind(k.id), {
          done: `Categoria „${k.name}” a fost ștearsă`,
          fallback: "Eroare la ștergerea categoriei",
        });
      }
    },

    // Models
    async addModel() {
      const name = this.newModelName.trim();
      if (!this.newModelKind || !name) return;
      const ok = await this.change(() => api.createCatalogModel({ kind_id: this.newModelKind, name }), {
        done: "Modelul a fost adăugat",
        fallback: "Eroare la adăugarea modelului",
      });
      if (ok) this.newModelName = "";
    },
    startEditModel(m) {
      this.editingModelId = m.id;
      this.editModelKind = m.kind_id;
      this.editModelName = m.name;
    },
    async saveModel(m) {
      const body = {};
      if (this.editModelKind !== m.kind_id) body.kind_id = this.editModelKind;
      if (this.editModelName.trim() !== m.name) body.name = this.editModelName;
      if (!Object.keys(body).length) {
        this.editingModelId = null;
        return;
      }
      const ok = await this.change(() => api.updateCatalogModel(m.id, body), {
        done: "Modelul a fost salvat; echipamentele existente își păstrează textul",
        fallback: "Eroare la salvarea modelului",
      });
      if (ok) this.editingModelId = null;
    },
    async removeModel(m) {
      const choice = await this.$refs.remove.ask({
        title: `Ștergeți „${m.fullName}” din catalog?`,
        text: "Nu va mai fi sugerat. Echipamentele care au deja acest model nu se schimbă.",
        canDelete: true,
      });
      if (choice === "delete") {
        await this.change(() => api.deleteCatalogModel(m.id), {
          done: "Modelul a fost șters din catalog",
          fallback: "Eroare la ștergerea modelului",
        });
      }
    },
  },
};
</script>

<style scoped>
.cat__count {
  margin-left: 2px;
  padding: 0 9px;
  border-radius: 999px;
  background: var(--mint-50);
  border: 1px solid var(--line);
  color: var(--ink-2);
  font-family: var(--font-ui);
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1.6;
}
.cat__add {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.cat__search {
  margin-bottom: 10px;
  max-width: none;
}
.cat__list {
  margin-bottom: 12px;
}
.cfg-row.cat__row {
  align-items: center;
  padding: 6px 8px 6px 14px;
  min-height: 50px;
}
.cat__usage {
  margin-top: 0;
}
.cat__edit {
  flex: 1 1 160px;
}
.cat__edit-model {
  flex: 1 1 260px;
  display: grid;
  grid-template-columns: minmax(110px, 2fr) 3fr;
  gap: 8px;
}
.cat__kind {
  font-weight: 700;
  color: var(--green);
}
.cat__model.cfg-row__name {
  font-weight: 600;
  font-size: 1rem;
}
</style>
